import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbAlertModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BezugsrechtAnnouncementListComponent } from '@zero/bezugsrecht/feature-bezugsrecht/web';
import { DepotFacade } from '@zero/depot/domain';
import { DirectDebitHintComponent, FirstSavingsPlanTeaserComponent } from '@zero/depot/feature-depot/web';
import { DiscoverCardComponent } from '@zero/discover/feature-discover/web';
import { CleverPushService } from '@zero/feature-clever-push/base';
import { GuidedTourFacade } from '@zero/guided-tour/domain';
import { GuidedTourComponent, GuidedTourTeaserComponent } from '@zero/guided-tour/feature-guided-tour/web';
import { LombardFacade } from '@zero/lombard/domain';
import {
  LombardConfirmationModalComponent,
  LombardConfirmationReminderComponent,
  LombardTeaserComponent,
} from '@zero/lombard/feature-lombard/web';
import { FirstActionStrategyModalComponent } from '@zero/onboarding/feature-investment-strategy/web';
import { OrdersComponent } from '@zero/order/feature-order/web';
import { ContractConfirmationFacade } from '@zero/shared/feature-contract-confirmation/base';
import { ContractConfirmationReminderComponent } from '@zero/shared/feature-contract-confirmation/web';
import { DividendReInvestHintComponent, DividendReceivedModalComponent } from '@zero/shared/feature-dividend-re-invest/web';
import { FormateIBANPipe } from '@zero/shared/ui-pipes';
import { UserNotesComponent } from '@zero/shared/ui-web-components';
import { CustomerStateFacade, PositionsFacade } from '@zero/shared/util-global-state';
import { PushMessageType } from '@zero/shared/util-models';
import { AuthService, DataService } from '@zero/shared/util-web-app-services';
import { TradingTimesService } from '@zero/trade/domain';
import { Subject, combineLatest, of, timer } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, switchMap, take, takeLast, takeUntil } from 'rxjs/operators';
import { AdAgreeNoteComponent } from '../../components/ad-agree-note/ad-agree-note.component';
import { CryptosComponent } from '../../components/cryptos/cryptos.component';
import { DepotHeaderComponent } from '../../components/depot-header/depot-header.component';
import { MultiOrderStrategiesSliderComponent } from '../../components/multi-order-strategies-slider/multi-order-strategies-slider.component';
import { MyMarketOverviewComponent } from '../../components/my-market-overview/my-market-overview.component';
import { MyTopInstrumentsComponent } from '../../components/my-top-instruments/my-top-instruments.component';
import { PositionsComponent } from '../../components/positions/positions.component';
import { SovendusComponent } from '../../components/sovendus/sovendus.component';

@Component({
  templateUrl: './home-page.component.html',
  imports: [
    DepotHeaderComponent,
    ContractConfirmationReminderComponent,
    NgbAlertModule,
    BezugsrechtAnnouncementListComponent,
    UserNotesComponent,
    MyMarketOverviewComponent,
    MyTopInstrumentsComponent,
    DiscoverCardComponent,
    FirstSavingsPlanTeaserComponent,
    SovendusComponent,
    PositionsComponent,
    CryptosComponent,
    AsyncPipe,
    TranslocoPipe,
    LombardConfirmationReminderComponent,
    LombardTeaserComponent,
    OrdersComponent,
    FormateIBANPipe,
    MultiOrderStrategiesSliderComponent,
    AdAgreeNoteComponent,
    DividendReInvestHintComponent,
    NgTemplateOutlet,
    GuidedTourTeaserComponent,
    GuidedTourComponent,
  ],
})
export class HomePageComponent implements OnInit, OnDestroy {
  public readonly positionsFacade = inject(PositionsFacade);
  public readonly contractConfirmationFacade = inject(ContractConfirmationFacade);
  public readonly lombardFacade = inject(LombardFacade);
  public readonly customerStateFacade = inject(CustomerStateFacade);
  public readonly depotFacade = inject(DepotFacade);
  readonly #dataService = inject(DataService);
  readonly #tradingTimesService = inject(TradingTimesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #modal = inject(NgbModal);
  readonly #cleverPushService = inject(CleverPushService);
  readonly #authService = inject(AuthService);
  readonly #guidedTourFacade = inject(GuidedTourFacade);

  public negativeAmountAlertClosed: boolean;
  public shouldDisplaySovendusIntegration: boolean;
  public showAdAgreeNote = false;
  public showIbanAlertClosed: boolean;
  public userNotesLoaded$ = new Subject<number>();
  public readonly showCryptos$ = of(true);
  public readonly isPotentialCustomer$ = this.#authService.isPotentialCustomer().pipe(map((isPotentialCustomer) => !!isPotentialCustomer));

  public ngOnInit(): void {
    this.#tradingTimesService.initHolidays();
    this.positionsFacade.loadPositions(true);
    this.positionsFacade.subscribeToPositionQuotes();
    this.depotFacade.loadUserInterests();
    this.#guidedTourFacade.loadCheckNotifications();
    this.#listenToAdAgreeNote();
    this.#listenToOrders();
    this.#listenToLombard();
    this.#listenToDirectDebitHint();
    this.#listenToDividendReceivedHint();
    this.#listenToFirstActionStrategyHint();
    this.#trackEventAfterLogin();
  }

  public ngOnDestroy(): void {
    this.positionsFacade.unsubscribeFromPositionQuotes();
  }

  public reloadPositions(): void {
    this.positionsFacade.loadPositions(true);
  }

  #listenToLombard(): void {
    this.customerStateFacade.customerBaseData$
      .pipe(
        map((cbd) => cbd?.lombardEnabled),
        distinctUntilChanged(),
        filter((lombardEnabled) => lombardEnabled),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.lombardFacade.loadLombard());

    this.lombardFacade.shouldDisplayConfirmationModal$
      .pipe(
        filter((shouldDisplay) => !!shouldDisplay),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#modal.open(LombardConfirmationModalComponent, { size: 'lg' }));
  }

  #listenToOrders(): void {
    this.#dataService.msgSubject
      .pipe(
        filter((msg) => msg?.type === PushMessageType.order),
        debounceTime(1000),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.reloadPositions());
  }

  #trackEventAfterLogin() {
    history?.state?.redirectAfterLogin && this.#cleverPushService.trackEvent('Home_Besucht_nach_Login');
  }

  #listenToDirectDebitHint(): void {
    this.depotFacade.displayDirectDebitHint$
      .pipe(
        filter((displayDirectDebitHint) => !!displayDirectDebitHint),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#modal.open(DirectDebitHintComponent, { size: 'lg' }));
  }

  #listenToDividendReceivedHint(): void {
    this.depotFacade.dividendReceivedHint$
      .pipe(
        filter((dividendReceivedHint) => !!dividendReceivedHint),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#modal.open(DividendReceivedModalComponent, { size: 'lg', centered: true }));
  }

  #listenToFirstActionStrategyHint(): void {
    this.depotFacade.firstActionStrategyHint$
      .pipe(
        filter((firstActionStrategyHint) => !!firstActionStrategyHint),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#modal.open(FirstActionStrategyModalComponent, { size: 'md', centered: true }));
  }

  #listenToAdAgreeNote(): void {
    combineLatest([
      this.customerStateFacade.customerAccountData$.pipe(filter((val) => val && val.intradayAmount >= 0 && val.lastImportedSaldo !== 0)),
      this.userNotesLoaded$.pipe(filter((notesCount) => notesCount === 0)),
    ])
      .pipe(
        take(1),
        delay(5000),
        switchMap(() =>
          this.customerStateFacade.customerBaseData$.pipe(
            takeUntil(timer(1000)),
            takeLast(1),
            filter((cbd) => cbd && !cbd.adAgree && !cbd.adAgreeChanged),
          ),
        ),
        filter(() => this.#dataService.weMayShowAdAgreeNote()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => (this.showAdAgreeNote = true));
  }
}
